import * as React from 'react';
import URLX, { currentUrl, equalsUrl, isInternalUrl, relativeUrl } from './Url';
import { scopeLinkAttribute, pushState, replaceState, scrollToHash, isExternalLink } from './history';

export type LinkProps = Pick<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  Exclude<keyof React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>
> & {
  to: URLX | string | { url: string };
  linkIdentifier?: string;
  replaceState?: boolean;
  skipPartialCache?: boolean;
  onPageLoaded?: (pageData: any) => void;
  onPageLoadFailed?: (error: any) => void;
  includeAppShellData?: boolean;
  innerRef?: React.Ref<HTMLAnchorElement>;
};

export async function onLinkClick(
  link: HTMLAnchorElement,
  e: React.MouseEvent<HTMLAnchorElement> | MouseEvent,
  linkProps?: LinkProps,
  deferLoadPromise?: Promise<any> | null,
) {
  if (window.IS_IN_EDIT_MODE || !/^http[s]?:/.test(link.href) || !isNormalClick(e) || isExternalLink(link)) {
    return Promise.resolve();
  }

  e.preventDefault();

  const options = {
    linkIdentifier: linkProps && linkProps.linkIdentifier,
    skipPartialCache: linkProps && linkProps.skipPartialCache,
    deferLoadPromise,
    includeAppShellData: linkProps && linkProps.includeAppShellData,
  };

  const promise =
    equalsUrl(currentUrl(), new URLX(link.href), true) || (linkProps && linkProps.replaceState)
      ? await replaceState(link.href, options)
      : await pushState(link.href, options);

  if (link.hash) {
    scrollToHash(link.hash);
  }

  return promise;
}

function isNormalClick(e: React.MouseEvent<HTMLAnchorElement> | MouseEvent) {
  return !(e.button > 1 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey);
}

export default class Link extends React.Component<LinkProps> {
  url: URLX | null = null;
  setUrlFromProps(props: LinkProps) {
    if (!props.to) {
      this.url = null;
    } else if (typeof props.to === 'string') {
      this.url = new URLX(props.to);
    } else if ('url' in props.to) {
      this.url = new URLX(props.to.url);
    } else {
      this.url = props.to;
    }
  }

  constructor(props: LinkProps) {
    super(props);
    this.setUrlFromProps(props);
  }
  UNSAFE_componentWillReceiveProps(nextProps: LinkProps) {
    if (this.props.to !== nextProps.to) {
      this.setUrlFromProps(nextProps);
    }
  }
  onClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    let deferLoadPromise: Promise<any> | null = null;
    if (this.props.onClick) {
      // TODO: Because Link inherits prop types from <a> we can't redefine
      // onClick's type until this:
      // https://github.com/Microsoft/TypeScript/issues/10727
      // has landed in TS
      deferLoadPromise = (this.props.onClick(e) as any) as Promise<any>;
      if (typeof deferLoadPromise !== 'object' || !deferLoadPromise.then) {
        deferLoadPromise = null;
      }
    }
    if (!e.isDefaultPrevented()) {
      onLinkClick(e.currentTarget, e, this.props, deferLoadPromise).then(
        result => {
          if (this.props.onPageLoaded) {
            this.props.onPageLoaded(result);
          }
        },
        result => {
          if (this.props.onPageLoadFailed) {
            this.props.onPageLoadFailed(result);
          }
        },
      );
    }
  };
  render() {
    const {
      to,
      linkIdentifier,
      // tslint:disable-next-line no-shadowed-variable
      replaceState,
      onPageLoaded,
      onPageLoadFailed,
      skipPartialCache,
      includeAppShellData,
      innerRef,
      ...props
    } = this.props;

    if (this.url && !isInternalUrl(this.url)) {
      return (
        <a {...props} ref={innerRef} href={relativeUrl(this.url)}>
          {this.props.children}
        </a>
      );
    } else {
      (props as any)[scopeLinkAttribute] = 'true';

      const exposedHref = this.url ? relativeUrl(this.url) : undefined;

      return (
        <a {...props} ref={innerRef} href={exposedHref} onClick={this.onClick}>
          {this.props.children}
        </a>
      );
    }
  }
}
